import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { buildCsv, downloadTextFile } from "@/lib/csv";
import { formatMoney } from "@/lib/money";
import { listTipActionAudit } from "@/lib/payments/admin-tips.functions";

export const Route = createFileRoute("/_authenticated/admin/tip-actions")({
  head: () => ({
    meta: [
      { title: "Manual tip actions — Quibblies admin" },
      {
        name: "description",
        content:
          "Audit trail of every manual refund and dispute recorded against a Quibblies tip, with the staff member responsible and the resulting receipt state.",
      },
      { property: "og:title", content: "Manual tip actions — Quibblies admin" },
      {
        property: "og:description",
        content: "Who refunded or disputed which tip, why, and what happened next.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TipActionsPage,
});

const LABELS: Record<string, string> = {
  refund: "Stripe refund issued",
  record_refund: "Refund recorded (handled elsewhere)",
  dispute: "Dispute recorded",
};

function TipActionsPage() {
  const load = useServerFn(listTipActionAudit);
  const [search, setSearch] = React.useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["tip-action-audit"],
    queryFn: () => load(),
    retry: false,
  });

  const rows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data ?? [];
    return (data ?? []).filter((row) =>
      [
        row.actorUsername,
        row.actorName,
        row.tipId,
        row.parameters.reason,
        row.receipt?.reference,
        row.receipt?.status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }, [data, search]);

  function exportCsv() {
    const csv = buildCsv(
      [
        "Recorded at",
        "Action",
        "Staff member",
        "Staff username",
        "Reason",
        "Applied",
        "Provider refund id",
        "Tip id",
        "Amount",
        "Currency",
        "Receipt status",
        "Payment reference",
        "Publication id",
        "Refunded at",
      ],
      rows.map((row) => [
        row.createdAt,
        row.parameters.action ?? row.action,
        row.actorName ?? "",
        row.actorUsername ?? "",
        row.parameters.reason ?? "",
        row.parameters.applied ? "yes" : "no",
        row.parameters.providerRefundId ?? "",
        row.tipId ?? "",
        row.receipt ? (row.receipt.amountMinor / 100).toFixed(2) : "",
        row.receipt?.currency ?? row.parameters.currency ?? "",
        row.receipt?.status ?? "",
        row.receipt?.reference ?? "",
        row.receipt?.publicationId ?? "",
        row.receipt?.refundedAt ?? "",
      ]),
    );
    downloadTextFile(
      `quibblies-tip-actions-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <SectionHeading
          title="Manual tip actions"
          description="This area is restricted to administrators."
        />
        <p className="mt-4 text-sm text-destructive">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Manual tip actions"
        description="Every refund or dispute a staff member recorded by hand: who did it, what they passed, and the state the tip ended up in."
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Input
          className="max-w-md"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by staff member, tip, reference, reason or status"
          aria-label="Search manual tip actions"
        />
        <Button variant="soft" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
          Export {rows.length} record{rows.length === 1 ? "" : "s"} as CSV
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : rows.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="No manual actions yet"
            description="Refunds and disputes recorded from the finance dashboard will appear here."
          />
        </div>
      ) : (
        <ul className="mt-8 space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="rounded-2xl border border-border/60 bg-surface/50 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium text-foreground">
                  {LABELS[row.parameters.action ?? ""] ??
                    (row.action === "tip.refunded" ? "Refund recorded" : "Dispute recorded")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(row.createdAt).toLocaleString()}
                </p>
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                By{" "}
                {row.actorUsername ? (
                  <Link
                    to="/u/$username"
                    params={{ username: row.actorUsername }}
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    {row.actorName ?? row.actorUsername}
                  </Link>
                ) : (
                  "an automated process"
                )}
                {row.parameters.amountMinor && row.parameters.currency
                  ? ` · ${formatMoney(row.parameters.amountMinor, row.parameters.currency)}`
                  : ""}
              </p>

              <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    Parameters
                  </dt>
                  <dd className="text-foreground">
                    Action: {row.parameters.action ?? "—"}
                    <br />
                    Reason: {row.parameters.reason ?? "None given"}
                    <br />
                    Applied: {row.parameters.applied ? "yes" : "no change"}
                    {row.parameters.providerRefundId ? (
                      <>
                        <br />
                        Provider refund:{" "}
                        <span className="font-mono text-xs">{row.parameters.providerRefundId}</span>
                      </>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    Resulting receipt
                  </dt>
                  <dd className="text-foreground">
                    {row.receipt ? (
                      <>
                        Status: {row.receipt.status}
                        <br />
                        Amount: {formatMoney(row.receipt.amountMinor, row.receipt.currency)}
                        <br />
                        Reference:{" "}
                        <span className="break-all font-mono text-xs">
                          {row.receipt.reference ?? "—"}
                        </span>
                        {row.receipt.publicationId ? (
                          <>
                            <br />
                            <Link
                              to="/story/$storyId"
                              params={{ storyId: row.receipt.publicationId }}
                              className="underline-offset-4 hover:underline"
                            >
                              View the publication
                            </Link>
                          </>
                        ) : null}
                      </>
                    ) : (
                      "The tip record is no longer available."
                    )}
                  </dd>
                </div>
              </dl>

              <p className="mt-3 break-all text-xs text-muted-foreground">Tip {row.tipId}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
